import React, { useMemo } from 'react';
import { shape, arrayOf, object, number, string } from 'prop-types';
import { contextPropTypes } from '../../../proptypes/shared';
import styles from './styles/styles.module.scss';
import HomeSearchBar from '../../search/HomeSearchBar';
import RotatingHero from '../../shared/RotatingHero';
import { isToday, betweenHours } from '../../../utils/time_helpers';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';

const {
  strings: {
    HOME_PAGE: {
      HOME_HEADER: { FIND_YOUR_NEXT_TRAIL, EXPLORE_NEARYBY_TRAILS }
    }
  }
} = StringConstantsMixin;

const FIRST_NAME_PLACEHOLDER = '{first_name}';

const chooseGreeting = (greetings, currentUser, greetingIndex) => {
  if (!(currentUser && greetings)) {
    return FIND_YOUR_NEXT_TRAIL;
  }

  let text = '';
  greetings.forEach(greet => {
    if (text.length === 0) {
      if (greet.event_start_date_time && isToday(greet.event_start_date_time)) {
        text = greet.generic_title[0];
      } else if (greet.daily_start_time && greet.daily_end_time && betweenHours(greet.daily_start_time, greet.daily_end_time)) {
        text = greet.custom_title[greetingIndex];
      }
    }
  });

  if (currentUser && currentUser.firstName && text.includes('{first_name}')) {
    text = text.replace(FIRST_NAME_PLACEHOLDER, currentUser.firstName);
  }

  return text;
};

const HomeHeader = ({ windowWidth, latLng, context, greetings, heroData }) => {
  const isCurrentUser = useMemo(() => {
    return context.currentUser !== null;
  }, [context]);

  const deviceType = useMemo(() => {
    return context.mobileBrowser || (windowWidth && windowWidth <= DESKTOP_BREAKPOINT) ? 'mobile' : 'desktop';
  }, [windowWidth]);

  const imageUrls = useMemo(() => {
    return heroData.heroImages.map(image => image[deviceType]);
  }, [windowWidth]);

  const headerText = useMemo(() => {
    return chooseGreeting(greetings, context.currentUser, heroData.greetingIndex);
  }, [context.currentUser]);

  if (isCurrentUser) {
    const buttonContainerStyle = deviceType === 'mobile' ? { marginBottom: 36 } : null;

    return (
      <RotatingHero duration={6000} imageUrls={imageUrls} className={styles.loggedInContainer} buttonContainerStyle={buttonContainerStyle}>
        <div className={styles.opacCover} />
        <h1 className={`${styles.header} xlate-none`}>{headerText}</h1>
        <div className={`${styles.searchBarContainer} ${styles.loggedInSearchContainer}`}>
          <HomeSearchBar windowWidth={windowWidth} latLng={latLng} context={context} />
        </div>
      </RotatingHero>
    );
  }

  return (
    <RotatingHero duration={6000} imageUrls={imageUrls} className={styles.container}>
      <div className={styles.opacCover} />
      <div className={styles.contentContainer}>
        <h1 className={`${styles.header} xlate-none`}>{headerText}</h1>
        <div className={styles.searchBarContainer}>
          <HomeSearchBar windowWidth={windowWidth} latLng={latLng} context={context} />
        </div>
        <div className={styles.exploreLink}>
          <a href={LanguageSupportUtil.wrapUrlSafe('/explore?ref=homepage_explore_homeheader', context.languageRegionCode)}>
            {EXPLORE_NEARYBY_TRAILS}
          </a>
        </div>
      </div>
    </RotatingHero>
  );
};

HomeHeader.propTypes = {
  windowWidth: number,
  latLng: arrayOf(number).isRequired,
  context: contextPropTypes.isRequired,
  greetings: arrayOf(object), // not required if logged out
  heroData: shape({
    greetingIndex: number,
    heroImages: arrayOf(
      shape({
        mobile: string,
        desktop: string
      })
    )
  }).isRequired
};

HomeHeader.defaultProps = {
  greetings: null,
  windowWidth: null
};

export default HomeHeader;
